import React from 'react';
import FeaturePage from './FeaturePage';
import { Box, Container, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HistoryIcon from '@mui/icons-material/History';
import FilterListIcon from '@mui/icons-material/FilterList';
import { motion } from 'framer-motion';


// Animation Variants
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: "easeOut" } }
};

function TaskManagementPage() {

  const taskFeatures = [
    { icon: <PlaylistAddCheckIcon color="primary"/>, text: "Create & Organize Tasks: Easily add new tasks with titles, detailed descriptions, and categories." },
    { icon: <PersonPinIcon color="primary"/>, text: "Assign Responsibility: Assign tasks to specific team members for clear accountability." },
    { icon: <EventAvailableIcon color="primary"/>, text: "Set Deadlines: Define assignment dates and due dates to keep projects on track." },
    { icon: <AssignmentTurnedInIcon color="primary"/>, text: "Track Progress: Monitor task status (Pending, Completed) and view completion dates." },
    { icon: <FilterListIcon color="primary"/>, text: "Filter & Search: Quickly find tasks using search terms and filter by status (Active/History)." },
    { icon: <HistoryIcon color="primary"/>, text: "Manage Task Lifecycle: Complete tasks, view history, and reopen tasks if needed." },
  ];

  return (
    <FeaturePage title="Task Management">
      <Container maxWidth="xl" disableGutters sx={{ py: { xs: 4, md: 6 } }}>

        {/* Introductory Text */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          style={{ marginBottom: '4rem', textAlign: 'center' }}
        >
          <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Stay Organized and On Schedule
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '850px', mx: 'auto' }}>
            Manage your dealership's workflow efficiently. Create, assign, and track tasks to ensure nothing falls through the cracks, from vehicle prep to follow-ups.
          </Typography>
        </motion.div>

        {/* Feature Section */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 6, md: 8 } }}>
          <motion.div variants={imageVariants} sx={{ mb: 4 }}>
             {/* ** Replace with a screenshot showing the tasks list/board ** */}
             <Paper
               elevation={6}
               sx={{
                 overflow: 'hidden',
                 borderRadius: '12px',
                 border: '1px solid rgba(0,0,0,0.08)',
                 maxWidth: '900px', // Adjust width as needed
                 mx: 'auto'
               }}
             >
             </Paper>
             <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                Visualize your team's workload and track progress in real-time.
             </Typography>
          </motion.div>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, textAlign: 'center' }}>Streamline Your Operations</Typography>
             <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
               Keep everyone aligned and accountable. Our task management system helps you prioritize work, meet deadlines, and improve overall efficiency.
             </Typography>
            <List>
              {taskFeatures.map((feature, index) => (
                <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                  <ListItem disablePadding sx={{ mb: 2, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
        </motion.div>

      </Container>
    </FeaturePage>
  );
}

export default TaskManagementPage; 